import './credit-report-modal.scss';

import { Button, Divider, Table, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { DownloadOutlined } from '@/media/antd-icons';
import { SYMBOLS } from '@/shared/constants';
import { formatPhoneNumber } from '@/shared/services/formatters';
import { saveBlobAsFile } from '@/shared/services/helpers/files';
import type { ICreditReport } from '@/shared/types/scoring-info.types';

import Modal from '../modal';
import { getActiveCredits, isOverdueAmount } from './helpers/credits';

/** Имя файла исходного отчёта ПКБ. */
const REPORT_FILE_NAME = 'pkb-report.html';

interface IProps {
  open: boolean;
  onClose: () => void;
  report: ICreditReport;
  /** Исходный отчёт ПКБ в HTML: есть — можно скачать файлом. */
  reportHtml?: string;
}

/** Пара «подпись — значение» в сером блоке сведений о клиенте. */
const Fact: React.FC<{ label: string; value?: string | number }> = ({
  label,
  value
}) => (
  <div className="credit-report-modal__fact">
    <dt className="credit-report-modal__fact-label">{label}</dt>
    <dd className="credit-report-modal__fact-value">
      {value || SYMBOLS.longDash}
    </dd>
  </div>
);

/** Кредитный отчёт ПКБ по клиенту: сведения, адреса, сводка, кредиты и госреестры. */
const CreditReportModal: React.FC<IProps> = ({
  open,
  onClose,
  report,
  reportHtml
}) => {
  const { t } = useTranslation();
  const { person, contacts, summary } = report;

  const downloadReportHtml = (): void => {
    if (!reportHtml) return;
    saveBlobAsFile(
      new Blob([reportHtml], { type: 'text/html' }),
      REPORT_FILE_NAME
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('scoringInfo.creditReport.title')}
      width={1200}
    >
      <div className="credit-report-modal">
        <div className="credit-report-modal__header">
          <Typography.Text type="secondary">
            {t('scoringInfo.creditReport.generatedAt', {
              date: report.reportDate
            })}
          </Typography.Text>
          {reportHtml && (
            <Button
              icon={<DownloadOutlined />}
              iconPlacement="end"
              onClick={downloadReportHtml}
            >
              {t('scoringInfo.creditReport.downloadHtml')}
            </Button>
          )}
        </div>

        <div className="credit-report-modal__client">
          <div className="credit-report-modal__client-main">
            <p className="credit-report-modal__client-name">
              {person.fullName}
            </p>
            <p className="credit-report-modal__client-iin">
              {t('scoringInfo.creditReport.iin', { iin: person.iin })}
            </p>
            <div className="credit-report-modal__badges">
              <Tag color="success" variant="solid">
                {t('scoringInfo.creditReport.activeCount', {
                  count: summary.active?.count || SYMBOLS.longDash
                })}
              </Tag>
              <Tag color="error" variant="solid">
                {t('scoringInfo.creditReport.overdueBadge', {
                  amount: summary.active?.totalOverdue || SYMBOLS.longDash
                })}
              </Tag>
            </div>
          </div>

          <dl className="credit-report-modal__facts">
            <Fact
              label={t('scoringInfo.creditReport.birthDate')}
              value={person.birthDate}
            />
            <Fact
              label={t('scoringInfo.creditReport.gender')}
              value={person.gender}
            />
          </dl>
          <dl className="credit-report-modal__facts">
            <Fact
              label={t('scoringInfo.creditReport.maritalStatus')}
              value={person.maritalStatus}
            />
            <Fact
              label={t('scoringInfo.creditReport.education')}
              value={person.education}
            />
          </dl>
          <dl className="credit-report-modal__facts">
            <Fact
              label={t('scoringInfo.creditReport.phones')}
              value={contacts.phones
                .map((phone) => formatPhoneNumber(phone))
                .join(', ')}
            />
            <Fact
              label={t('scoringInfo.creditReport.dependents')}
              value={contacts.dependentsUnder18}
            />
          </dl>
        </div>

        <Divider />

        <section>
          <h3 className="credit-report-modal__section-title">
            {t('scoringInfo.creditReport.addresses.title')}
          </h3>
          <Table
            size="small"
            rowKey={(row) => row.date + row.type}
            dataSource={report.addresses}
            pagination={false}
            scroll={{ x: 'max-content' }}
            columns={[
              {
                title: t('scoringInfo.creditReport.addresses.type'),
                dataIndex: 'type'
              },
              {
                title: t('scoringInfo.creditReport.addresses.street'),
                dataIndex: 'street',
                render: (street: string) => (
                  <span className="credit-report-modal__long-text">
                    {street}
                  </span>
                )
              },
              {
                title: t('scoringInfo.creditReport.addresses.region'),
                dataIndex: 'region'
              },
              {
                title: t('scoringInfo.creditReport.addresses.date'),
                dataIndex: 'date'
              }
            ]}
          />
        </section>

        <Divider />

        <section>
          <h3 className="credit-report-modal__section-title">
            {t('scoringInfo.creditReport.summary.title')}
          </h3>
          <dl className="credit-report-modal__summary">
            <Fact
              label={t('scoringInfo.creditReport.summary.active')}
              value={summary.active?.count}
            />
            <Fact
              label={t('scoringInfo.creditReport.summary.outstanding')}
              value={summary.active?.totalOutstanding}
            />
            <Fact
              label={t('scoringInfo.creditReport.summary.penalty')}
              value={summary.active?.penalty}
            />
            <div className="credit-report-modal__summary-overdue">
              <Fact
                label={t('scoringInfo.creditReport.summary.overdue')}
                value={summary.active?.totalOverdue}
              />
            </div>
          </dl>
        </section>

        <Divider />

        <section>
          <h3 className="credit-report-modal__section-title">
            {t('scoringInfo.creditReport.credits.title')}
          </h3>
          <Table
            size="small"
            rowKey="contractCode"
            dataSource={getActiveCredits(report)}
            pagination={false}
            scroll={{ x: 'max-content' }}
            columns={[
              {
                title: t('scoringInfo.creditReport.credits.number'),
                dataIndex: 'contractCode'
              },
              {
                title: t('scoringInfo.creditReport.credits.status'),
                dataIndex: 'status',
                render: (status: string) => (
                  <span className="credit-report-modal__long-text credit-report-modal__long-text--narrow">
                    {status}
                  </span>
                )
              },
              {
                title: t('scoringInfo.creditReport.credits.purpose'),
                dataIndex: 'purpose'
              },
              {
                title: t('scoringInfo.creditReport.credits.start'),
                dataIndex: 'startDate'
              },
              {
                title: t('scoringInfo.creditReport.credits.end'),
                dataIndex: 'endDate'
              },
              {
                title: t('scoringInfo.creditReport.credits.overdue'),
                dataIndex: 'overdue',
                render: (overdue: string) =>
                  isOverdueAmount(overdue) ? (
                    <Typography.Text type="danger" strong>
                      {overdue}
                    </Typography.Text>
                  ) : (
                    overdue
                  )
              },
              {
                title: t('scoringInfo.creditReport.credits.rate'),
                dataIndex: 'effectiveRate'
              }
            ]}
          />
        </section>

        <Divider />

        <section>
          <h3 className="credit-report-modal__section-title">
            {t('scoringInfo.creditReport.registries.title')}
          </h3>
          <Table
            size="small"
            rowKey={(row) => row.type + row.actualDate}
            dataSource={report.publicSources}
            pagination={false}
            scroll={{ x: 'max-content' }}
            columns={[
              {
                title: t('scoringInfo.creditReport.registries.source'),
                dataIndex: 'type'
              },
              {
                title: t('scoringInfo.creditReport.registries.status'),
                dataIndex: 'status',
                render: (status: string) => (
                  <span className="credit-report-modal__long-text credit-report-modal__long-text--wide">
                    {status}
                  </span>
                )
              },
              {
                title: t('scoringInfo.creditReport.registries.actualDate'),
                dataIndex: 'actualDate'
              }
            ]}
          />
        </section>

        <div className="credit-report-modal__footer">
          <Button onClick={onClose}>{t('common.close')}</Button>
        </div>
      </div>
    </Modal>
  );
};

export default CreditReportModal;
